/**
 * Form Template Service
 *
 * Handles API calls for form template management including:
 * - Creating templates from form submissions
 * - Retrieving available templates
 * - Applying templates to forms
 * - Managing template permissions
 */

import apiClient from './apiClient';
import dynamicFormsService from './dynamicFormsService';

class FormTemplateService {
    constructor() {
        this.baseURL = '/dynamic-forms';
        // simple cache + pending map to dedupe repeated calls
        this.cache = new Map();
        this.pending = new Map();
        this.cacheTTL = 60 * 1000; // 60s is enough for UI sessions
    }

    sanitizeVersion(version) {
        if (version == null || version === '') return null;
        if (typeof version === 'number') return `${version.toFixed(1)}`;
        const trimmed = String(version).trim().replace(/^v/i, '');
        if (/^\d+$/.test(trimmed)) return `${trimmed}.0`;
        return trimmed;
    }

    getCached(key) {
        const entry = this.cache.get(key);
        if (!entry) return null;
        if (Date.now() - entry.t > this.cacheTTL) {
            this.cache.delete(key);
            return null;
        }
        return entry.v;
    }

    setCached(key, value) {
        this.cache.set(key, { v: value, t: Date.now() });
    }

    dedupe(key, factory) {
        if (this.pending.has(key)) return this.pending.get(key);
        const p = factory().finally(() => this.pending.delete(key));
        this.pending.set(key, p);
        return p;
    }

    /**
     * Create a new form template
     * @param {string} formId - The form ID
     * @param {Object} templateData - Template data (name, description, form_data, etc.)
     * @returns {Promise<Object>} API response
     */
    async createTemplate(formId, templateData) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/forms/${formId}/templates`,
                templateData
            );

            return {
                success: true,
                data: response.data,
                message: 'Template created successfully'
            };
        } catch (error) {
            console.error('Error creating template:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to create template',
                data: null
            };
        }
    }

    /**
     * Get all templates for a specific form
     * @param {string} formId - The form ID
     * @returns {Promise<Object>} API response with templates list
     */
    async getFormTemplates(formId) {
        try {
            const response = await apiClient.get(
                `${this.baseURL}/forms/${formId}/templates`
            );

            return {
                success: true,
                data: response.data,
                message: 'Templates retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching form templates:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch templates',
                data: []
            };
        }
    }

    /**
     * Get all templates accessible to the current user
     * @returns {Promise<Object>} API response with templates list
     */
    async getAllTemplates() {
        try {
            const response = await apiClient.get(`${this.baseURL}/templates`);

            return {
                success: true,
                data: response.data,
                message: 'Templates retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching all templates:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch templates',
                data: []
            };
        }
    }

    /**
     * Get a specific template by ID
     * @param {string} templateId - The template ID
     * @returns {Promise<Object>} API response with template data
     */
    async getTemplate(templateId) {
        try {
            const response = await apiClient.get(
                `${this.baseURL}/templates/${templateId}`
            );

            return {
                success: true,
                data: response.data,
                message: 'Template retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching template:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch template',
                data: null
            };
        }
    }

    /**
     * Update an existing template
     * @param {string} templateId - The template ID
     * @param {Object} updateData - Updated template data
     * @returns {Promise<Object>} API response
     */
    async updateTemplate(templateId, updateData) {
        try {
            const response = await apiClient.put(
                `${this.baseURL}/templates/${templateId}`,
                updateData
            );

            return {
                success: true,
                data: response.data,
                message: 'Template updated successfully'
            };
        } catch (error) {
            console.error('Error updating template:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to update template',
                data: null
            };
        }
    }

    /**
     * Delete a template
     * @param {string} templateId - The template ID
     * @returns {Promise<Object>} API response
     */
    async deleteTemplate(templateId) {
        try {
            await apiClient.delete(`${this.baseURL}/templates/${templateId}`);

            return {
                success: true,
                message: 'Template deleted successfully'
            };
        } catch (error) {
            console.error('Error deleting template:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to delete template'
            };
        }
    }

    /**
     * Apply a template to get form data
     * @param {string} templateId - The template ID
     * @returns {Promise<Object>} API response with form data
     */
    async applyTemplate(templateId) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/templates/${templateId}/apply`
            );

            return {
                success: true,
                data: response.data,
                message: 'Template applied successfully'
            };
        } catch (error) {
            console.error('Error applying template:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to apply template',
                data: null
            };
        }
    }

    /**
     * Get template usage statistics
     * @param {string} templateId - The template ID
     * @returns {Promise<Object>} API response with usage stats
     */
    async getTemplateStats(templateId) {
        try {
            const response = await apiClient.get(
                `${this.baseURL}/templates/${templateId}/stats`
            );

            return {
                success: true,
                data: response.data,
                message: 'Template stats retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching template stats:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch template stats',
                data: null
            };
        }
    }

    /**
     * Search templates by name or description
     * @param {string} query - Search query
     * @param {string} formId - Optional form ID to filter by
     * @returns {Promise<Object>} API response with matching templates
     */
    async searchTemplates(query, formId = null) {
        try {
            const params = new URLSearchParams({ query });
            if (formId) {
                params.append('form_id', formId);
            }

            const response = await apiClient.get(
                `${this.baseURL}/templates/search?${params.toString()}`
            );

            return {
                success: true,
                data: response.data,
                message: 'Template search completed successfully'
            };
        } catch (error) {
            console.error('Error searching templates:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to search templates',
                data: []
            };
        }
    }

    /**
     * Share a template with specific users or make it public
     * @param {string} templateId - The template ID
     * @param {Object} shareData - Sharing configuration
     * @returns {Promise<Object>} API response
     */
    async shareTemplate(templateId, shareData) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/templates/${templateId}/share`,
                shareData
            );

            return {
                success: true,
                data: response.data,
                message: 'Template shared successfully'
            };
        } catch (error) {
            console.error('Error sharing template:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to share template',
                data: null
            };
        }
    }

    /**
     * Duplicate an existing template
     * @param {string} templateId - The template ID to duplicate
     * @param {Object} duplicateData - New template data (name, description)
     * @returns {Promise<Object>} API response
     */
    async duplicateTemplate(templateId, duplicateData) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/templates/${templateId}/duplicate`,
                duplicateData
            );

            return {
                success: true,
                data: response.data,
                message: 'Template duplicated successfully'
            };
        } catch (error) {
            console.error('Error duplicating template:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to duplicate template',
                data: null
            };
        }
    }

    /**
     * Get templates by category or department
     * @param {Object} filters - Filter criteria (department_id, category_id, etc.)
     * @returns {Promise<Object>} API response with filtered templates
     */
    async getTemplatesByFilter(filters) {
        try {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const response = await apiClient.get(
                `${this.baseURL}/templates?${params.toString()}`
            );

            return {
                success: true,
                data: response.data,
                message: 'Filtered templates retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching filtered templates:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch filtered templates',
                data: []
            };
        }
    }

    // ========================================
    // FORM VERSION MANAGEMENT METHODS
    // ========================================

    /**
     * Create a new version of a form
     * @param {string} formId - The form ID
     * @param {Object} versionData - Version creation data
     * @returns {Promise<Object>} API response
     */
    async createFormVersion(formId, versionData) {
        try {
            // Backend may reject empty body; ensure minimally valid payload
            const payload = versionData && typeof versionData === 'object' ? versionData : {};
            if (payload.version_notes == null) payload.version_notes = '';
            if (payload.changes == null || typeof payload.changes !== 'object') payload.changes = {};

            const response = await apiClient.post(
                `${this.baseURL}/forms/${formId}/versions`,
                payload
            );

            // Invalidate cached version info for this form
            this.cache.delete(`versions_${formId}`);
            this.cache.delete(`version_info_${formId}`);

            return {
                success: true,
                data: response.data,
                message: 'Form version created successfully'
            };
        } catch (error) {
            console.error('Error creating form version:', error?.response?.data || error?.message || error);
            return {
                success: false,
                error: error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to create form version',
                data: null
            };
        }
    }

    /**
     * Get all versions of a form
     * @param {string} formId - The form ID
     * @returns {Promise<Object>} API response with versions list
     */
    async getFormVersions(formId) {
        try {
            const key = `versions_${formId}`;
            const cached = this.getCached(key);
            if (cached) return cached;
            const response = await this.dedupe(key, () => apiClient.get(
                `${this.baseURL}/forms/${formId}/versions`
            ));

            const result = {
                success: true,
                data: response.data,
                message: 'Form versions retrieved successfully'
            };
            this.setCached(key, result);
            return result;
        } catch (error) {
            console.error('Error fetching form versions:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch form versions',
                data: { items: [], total: 0 }
            };
        }
    }

    /**
     * Get version information for a form
     * @param {string} formId - The form ID
     * @returns {Promise<Object>} API response with version info
     */
    async getFormVersionInfo(formId) {
        try {
            const key = `version_info_${formId}`;
            const cached = this.getCached(key);
            if (cached) return cached;

            // Add cache-busting parameter to force fresh data on test server
            const response = await this.dedupe(key, () => apiClient.get(
                `${this.baseURL}/forms/${formId}/versions/info`,
                { params: { _t: Date.now() } }
            ));

            const result = {
                success: true,
                data: response.data,
                message: 'Form version info retrieved successfully'
            };
            this.setCached(key, result);
            return result;
        } catch (error) {
            console.error('Error fetching form version info:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch form version info',
                data: { versions: [], latest_version: null, total_versions: 0 }
            };
        }
    }

    /**
     * Check if form has multiple versions
     * @param {string} formId - The form ID
     * @returns {Promise<boolean>} True if form has multiple versions
     */
    async hasMultipleVersions(formId) {
        try {
            const result = await this.getFormVersionInfo(formId);
            return result.success && result.data.total_versions > 1;
        } catch (error) {
            console.error('Error checking form versions:', error);
            return false;
        }
    }

    /**
     * Get latest version of a form
     * @param {string} formId - The form ID
     * @returns {Promise<string|null>} Latest version string or null
     */
    async getLatestVersion(formId) {
        try {
            const result = await this.getFormVersionInfo(formId);
            return result.success ? result.data.latest_version : null;
        } catch (error) {
            console.error('Error getting latest version:', error);
            return null;
        }
    }

    /**
     * Get version information for multiple forms in a single request
     * @param {Array<string>} formIds - Array of form IDs
     * @returns {Promise<Object>} API response with bulk version data
     */
    async getBulkFormVersionInfo(formIds) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/forms/bulk/versions/info`,
                formIds
            );

            return {
                success: true,
                data: response.data.data, // Nested data structure from API
                total: response.data.total_forms,
                processed: response.data.processed_forms,
                message: 'Bulk form version info retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching bulk form version info:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch bulk form version info',
                data: {}
            };
        }
    }

    /**
     * Activate a specific version of a form (make it the latest version for nurses)
     * @param {string} formId - The form ID
     * @param {string} version - The version to activate
     * @returns {Promise<Object>} API response
     */
    async activateFormVersion(formId, version) {
        try {
            const response = await apiClient.put(
                `${this.baseURL}/forms/${formId}/versions/${version}/activate`
            );

            // Invalidate ALL cached data for this form to prevent stale version display
            this.cache.delete(`versions_${formId}`);
            this.cache.delete(`version_info_${formId}`);
            this.cache.delete(`form_${formId}`);  // Clear form data cache

            // ✅ FIX: Also clear cache in dynamicFormsService to ensure Fill Form page gets latest version
            dynamicFormsService.clearFormCache(formId);

            return {
                success: true,
                data: response.data,
                message: `Form version ${version} activated successfully`
            };
        } catch (error) {
            console.error('Error activating form version:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to activate form version',
                data: null
            };
        }
    }
}

// Create and export a singleton instance
const formTemplateService = new FormTemplateService();
export { formTemplateService };
export default formTemplateService;